/**
 * Revalidate when the user comes back to the tab, or when the network does.
 *
 * This is the counterpart to realtime, not a lesser version of it:
 *
 * - Browsers throttle a background tab's timers to roughly once a minute. That
 *   stalls the realtime client's 25s heartbeat, the socket times out, and the
 *   events from the gap are gone — postgres_changes has no replay. So even a
 *   fully-subscribed surface needs this to be correct after a tab return.
 * - For data whose table is not in the `supabase_realtime` publication there is
 *   no subscription to be had at all, and this is the only tool available.
 *
 * `visibilitychange` and `online` only — deliberately not `focus`, which fires
 * on every click back from a devtools panel or a dialog and would turn idle
 * fiddling into database load.
 */

/** Matches DocumentStore's PROJECT_DOC_CACHE_TTL_MS, the app's existing floor. */
export const REVALIDATE_MIN_INTERVAL_MS = 60_000;

export interface FocusRevalidationOptions {
  /** Minimum gap between runs. Defaults to REVALIDATE_MIN_INTERVAL_MS. */
  minIntervalMs?: number;
}

/**
 * Run `fn` on tab return and on reconnect, at most once per `minIntervalMs`.
 * Returns a teardown that removes both listeners.
 */
export function registerFocusRevalidation(
  fn: () => void | Promise<void>,
  opts: FocusRevalidationOptions = {}
): () => void {
  const minIntervalMs = opts.minIntervalMs ?? REVALIDATE_MIN_INTERVAL_MS;

  // Seeded with registration time, not 0: the caller has just fetched, so an
  // alt-tab three seconds later should not refetch what is already on screen.
  let lastRun = Date.now();
  let trailing: ReturnType<typeof setTimeout> | null = null;

  const run = () => {
    const wait = minIntervalMs - (Date.now() - lastRun);
    if (wait > 0) {
      // Deferred, never dropped. A tab return inside the window still means the
      // socket may have missed events, and the user who comes back once and
      // then stays would otherwise get no catch-up at all — the throttle is
      // there to cap the rate, not to skip the revalidation.
      if (trailing === null) {
        trailing = setTimeout(() => {
          trailing = null;
          // A tab hidden again by the time the window closes gets nothing out of
          // this run but a burnt `lastRun`, which would then throttle the
          // revalidation on the user's actual return. Dropping it loses nothing:
          // `lastRun` is by now older than the window, so the visibilitychange
          // that brings the tab back runs immediately.
          if (document.visibilityState === "hidden") return;
          run();
        }, wait);
      }
      return;
    }
    try {
      const result = fn();
      // An async revalidation rejects long after this returns, so its failure
      // is only logged if the promise is picked up here.
      if (result instanceof Promise) {
        result.catch((e) => {
          console.error("[realtime] focus revalidation rejected", e);
        });
      }
    } catch (e) {
      // Caught rather than left to escape: this also runs from a timer, where
      // an exception is an uncaught error with no context.
      console.error("[realtime] focus revalidation failed", e);
    } finally {
      // Recorded even when the run failed: the throttle caps the rate, and a
      // consistently throwing `fn` would otherwise run on every focus and
      // online event for as long as it keeps failing. The retry is not lost —
      // the next return defers to a trailing run one window later.
      lastRun = Date.now();
    }
  };

  const onVisibility = () => {
    if (document.visibilityState === "visible") run();
  };

  // `online` fires in a background tab too, where the same reasoning as the
  // trailing run applies: nobody is looking, and running would burn `lastRun`,
  // so the user's actual return is throttled and its catch-up deferred a full
  // window. Dropped rather than deferred — `lastRun` is by then older than the
  // window, so the visibilitychange that brings the tab back runs immediately.
  const onOnline = () => {
    if (document.visibilityState === "hidden") return;
    run();
  };

  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("online", onOnline);

  return () => {
    if (trailing !== null) clearTimeout(trailing);
    trailing = null;
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("online", onOnline);
  };
}
